import { useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveTemplate } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useGallery,
  useProperty,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { CSSProperties } from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../utils/heroImageUtils';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
  /**
   * Optional large headline shown beneath the (now smaller) title/eyebrow.
   * When set, the header adopts the eyebrow + headline treatment; when
   * omitted the title renders large on its own (legacy look).
   */
  headline?: string;
  /** Show the pill CTA button. */
  showCta?: boolean;
  /** Default button label (editable per-page via the `cta_link` slot group). */
  ctaLabel?: string;
  /** Default button destination (editable per-page via the `cta_link` slot group). */
  ctaUrl?: string;
  /**
   * Optional property field name to bind as the CTA URL's default. Useful when
   * the destination is a per-property link (e.g. `property_resident_portal_url`)
   * rather than a static in-site path.
   */
  ctaUrlField?: string;
  /** Anchor `target` for the CTA (defaults to `_self`). Use `_blank` for offsite links. */
  ctaTarget?: string;
  /**
   * Optional default heading rendered alongside the blurb (two-column layout).
   * Editable per-page via the `blurb_heading` token.
   */
  blurbHeading?: string;
  /**
   * When true, render phone + email pill CTAs beneath the blurb heading in
   * the left column (used on the Contact Us page).
   */
  showBlurbCtas?: boolean;
  /**
   * Optional per-page override for the email/secondary CTA button label
   * (editable per-page via the `email_cta.label` slot group field).
   * Defaults to "Email Us" when omitted.
   */
  emailCtaLabel?: string;
  /**
   * Optional per-page override for the email/secondary CTA URL. When set,
   * the URL field is treated as a plain URL (not an email address) — use
   * this to point the button at an internal page like "/contact-us" while
   * keeping the same slot group.
   */
  emailCtaUrl?: string;
  /**
   * Which icon to render inside the email/secondary CTA. Defaults to the
   * envelope; set to "chat" for a Contact Us speech-bubble.
   */
  emailCtaIcon?: 'email' | 'chat';
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toMailtoUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('mailto:')) return raw;
  return raw.includes('@') ? `mailto:${raw}` : raw;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function hasBlurbContent(value: string | null | undefined): boolean {
  return Boolean(stripHtml(value || '').trim());
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Page hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Hero CTA Button',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: '',
    },
    url: {
      label: 'Button URL',
      type: 'url',
      defaultValue: '#',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const phoneCtaDef: SlotGroupDefinition = {
  label: 'Phone CTA',
  fields: {
    label: {
      label: 'Button Text',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
    url: {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const emailCtaDef: SlotGroupDefinition = {
  label: 'Email CTA',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Email Us',
    },
    url: {
      label: 'Email Address',
      type: 'email',
      defaultField: 'property_email',
      suggestedFields: ['property_email'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
  headline,
  showCta = false,
  ctaLabel,
  ctaUrl,
  ctaUrlField,
  ctaTarget,
  blurbHeading,
  showBlurbCtas = false,
  emailCtaLabel,
  emailCtaUrl,
  emailCtaIcon = 'email',
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const headlinePath = `${prefix}.headline`;
  const blurbPath = `${prefix}.blurb`;
  const blurbHeadingPath = `${prefix}.blurb_heading`;
  const bgPath = `${prefix}.bg_image`;
  const ctaPath = `${prefix}.cta_link`;
  const phoneCtaPath = `${prefix}.phone_cta`;
  const emailCtaPath = `${prefix}.email_cta`;

  const blurbContent = useContentToken(blurbPath, {
    defaultField: blurbField,
    defaultValue: defaultText || '',
  });
  const showBlurb = hasBlurbContent(blurbContent);

  const property = useProperty() as Record<string, any>;
  const resolvedBlurbHeading = blurbHeading
    ? resolveTemplate(blurbHeading, property)
    : '';
  const blurbHeadingDefault = resolvedBlurbHeading ? `<p>${resolvedBlurbHeading}</p>` : '';
  const blurbHeadingContent = useContentToken(blurbHeadingPath, {
    defaultValue: blurbHeadingDefault,
  });
  const hasBlurbHeading = hasBlurbContent(blurbHeadingContent);

  const headlineDefault = headline ? `<p>${headline}</p>` : '';
  const headlineContent = useContentToken(headlinePath, { defaultValue: headlineDefault });
  const hasHeadline = hasBlurbContent(headlineContent);

  // Runtime CTA definition — mirrors `ctaLinkDef` structurally but folds in the
  // per-page defaults passed through pages.json props so the editor sidebar
  // prefills with the same values the rendered page sees.
  const runtimeCtaDef = useMemo<SlotGroupDefinition>(
    () => ({
      label: 'Hero CTA Button',
      fields: {
        label: {
          label: 'Button Text',
          type: 'text',
          defaultValue: ctaLabel || '',
        },
        url: {
          label: 'Button URL',
          type: 'url',
          ...(ctaUrlField ? { defaultField: ctaUrlField } : {}),
          defaultValue: ctaUrl || '#',
        },
        _target: { label: 'Target', type: 'text', defaultValue: ctaTarget || '_self' },
      },
    }),
    [ctaLabel, ctaUrl, ctaUrlField, ctaTarget],
  );
  const ctaLink = useSlotGroup(ctaPath, runtimeCtaDef);
  const ctaLabelValue = (ctaLink.label || '').trim();
  const ctaHref = (ctaLink.url || '').trim() || '#';
  const ctaTargetValue = ctaLink._target || '_self';
  const isExternalCta = ctaTargetValue === '_blank';
  const hasCta = showCta && Boolean(ctaLabelValue);

  const phoneCta = useSlotGroup(phoneCtaPath, phoneCtaDef);
  // Per-page override: callers can rename the second blurb button
  // (e.g. "Contact Us" on Apply Now) and/or repoint it at an internal page
  // instead of mailto:, without forking the slot group.
  const emailCtaInstanceDef = useMemo<SlotGroupDefinition>(() => {
    if (!emailCtaLabel && !emailCtaUrl) return emailCtaDef;
    const nextFields = { ...emailCtaDef.fields };
    if (emailCtaLabel) {
      nextFields.label = {
        ...emailCtaDef.fields.label,
        defaultValue: emailCtaLabel,
      };
    }
    if (emailCtaUrl) {
      // Switch the URL field from `email` to `url` so the editor's picker
      // offers URL-typed property fields and `toMailtoUrl` leaves the
      // value alone (paths like "/contact-us" have no "@").
      nextFields.url = {
        ...emailCtaDef.fields.url,
        type: 'url',
        defaultValue: emailCtaUrl,
        defaultField: undefined,
        suggestedFields: undefined,
      };
    }
    return { ...emailCtaDef, fields: nextFields };
  }, [emailCtaLabel, emailCtaUrl]);
  const emailCta = useSlotGroup(emailCtaPath, emailCtaInstanceDef);
  const phoneHref = toTelUrl(phoneCta.url);
  const phoneDisplay = formatPhone(phoneCta.label) || formatPhone(phoneCta.url);
  const emailHref = toMailtoUrl(emailCta.url);
  const emailDisplay = (emailCta.label || '').trim() || emailCta.url || '';
  const hasPhoneCta = showBlurbCtas && Boolean(phoneHref) && Boolean(phoneDisplay);
  const hasEmailCta = showBlurbCtas && Boolean(emailHref) && Boolean(emailDisplay);
  const hasBlurbCtas = hasPhoneCta || hasEmailCta;

  useSiteCtaStyles();

  const bgImage = useSlotGroup(bgPath, bgImageDef);
  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');

  const pageHint = prefix;
  const keywords = useMemo(
    () => title.toLowerCase().split(/\s+/).filter(Boolean),
    [title],
  );

  const photoIndex = useMemo(
    () => pickHeroImage(gallery, keywords, pageHint),
    [gallery, keywords, pageHint],
  );

  const displayImage =
    bgImage.image ||
    (photoIndex >= 0 ? gallery[photoIndex]?.image : null) ||
    featuredImage ||
    gallery[0]?.image ||
    null;

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="hero-solid-bg">
      {/* Photographic hero — title centered over featured image */}
      <div className="hero-solid-hero relative w-full overflow-hidden">
        <EditableSlotGroup groupPath={bgPath} definition={bgImageDef} className="absolute inset-0">
          {displayImage ? (
            <VibesitesImage
              url={displayImage}
              alt={bgImage.alt || title}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-[var(--vibe-color-text)]" />
          )}
        </EditableSlotGroup>

        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/25" />

        <div className="hero-solid-title-wrap relative z-10 flex h-full flex-col items-center justify-center px-6 sm:px-8 text-center text-white">
          {hasHeadline ? (
            <>
              <RichTextContent
                path={titlePath}
                defaultValue={`<p>${title}</p>`}
                as="p"
                className="hero-solid-eyebrow"
              />
              <RichTextContent
                path={headlinePath}
                defaultValue={headlineDefault}
                as="h1"
                className="hero-solid-headline"
              />
            </>
          ) : (
            <RichTextContent
              path={titlePath}
              defaultValue={`<p>${title}</p>`}
              as="h1"
              className="hero-solid-title"
            />
          )}

          {hasCta ? (
            <EditableSlotGroup groupPath={ctaPath} definition={runtimeCtaDef}>
              <a
                href={ctaHref}
                target={ctaTargetValue}
                rel={isExternalCta ? 'noopener noreferrer' : undefined}
                className="hero-solid-cta-wrap site-cta site-cta--secondary"
                style={
                  {
                    // Hero image bg + white text — swap the secondary fill to
                    // the theme primary so the button stays bold and legible.
                    '--site-cta-secondary-accent': 'var(--vibe-color-primary)',
                    '--site-cta-secondary-on-accent': 'var(--vibe-color-text-on-primary, #ffffff)',
                    '--site-cta-hover-accent': '#ffffff',
                    '--site-cta-hover-on-accent': 'var(--vibe-color-primary)',
                  } as CSSProperties
                }
              >
                <span>{ctaLabelValue}</span>
              </a>
            </EditableSlotGroup>
          ) : null}
        </div>
      </div>

      {showBlurb || hasBlurbHeading || hasBlurbCtas ? (
        <div className="hero-solid-blurb text-[var(--vibe-color-text)]">
          <div className="hero-solid-blurb-inner max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
            <div className="hero-solid-blurb-grid">
              <div className="hero-solid-blurb-heading-col">
                <RichTextContent
                  path={blurbHeadingPath}
                  defaultValue={blurbHeadingDefault}
                  as="h2"
                  className="hero-solid-blurb-heading"
                />
                {hasBlurbCtas ? (
                  <div className="hero-solid-blurb-ctas">
                    {hasPhoneCta ? (
                      <EditableSlotGroup groupPath={phoneCtaPath} definition={phoneCtaDef}>
                        <a
                          href={phoneHref}
                          target={phoneCta._target || '_self'}
                          className="dniphone dniphonehref site-cta site-cta--primary"
                        >
                          <span className="hero-solid-blurb-cta-icon" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path d="M7 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H7Zm5 18a1 1 0 1 1 0-2 1 1 0 0 1 0 2ZM7 4h10v13H7V4Z" fill="currentColor"/>
                            </svg>
                          </span>
                          <span>{phoneDisplay}</span>
                        </a>
                      </EditableSlotGroup>
                    ) : null}
                    {hasEmailCta ? (
                      <EditableSlotGroup groupPath={emailCtaPath} definition={emailCtaInstanceDef}>
                        <a
                          href={emailHref}
                          target={emailCta._target || '_self'}
                          className="site-cta site-cta--primary hero-solid-blurb-email-cta"
                        >
                          <span className="hero-solid-blurb-cta-icon" aria-hidden="true">
                            {emailCtaIcon === 'chat' ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9.4l-4.7 3.76A1 1 0 0 1 3 21V6a2 2 0 0 1 1-2Zm1 2v12.92L8.7 16H20V6H5Z" fill="currentColor"/>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm0 2v.4l8 5.2 8-5.2V7H4Zm16 2.35-7.45 4.84a1 1 0 0 1-1.1 0L4 9.35V17h16V9.35Z" fill="currentColor"/>
                              </svg>
                            )}
                          </span>
                          <span>{emailDisplay}</span>
                        </a>
                      </EditableSlotGroup>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="hero-solid-blurb-body-col">
                <RichTextContent
                  path={blurbPath}
                  defaultField={blurbField}
                  defaultValue={defaultText || ''}
                  as="div"
                  className="hero-solid-blurb-text font-body text-base md:text-lg leading-relaxed [&_a]:underline [&_a]:text-[var(--vibe-color-primary)]"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.headline"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb_heading"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.phone_cta"
        definition={phoneCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.email_cta"
        definition={emailCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .hero-solid-hero {
          /* Rail tile size matches the home hero (1/5 viewport), not this shorter hero. */
          --hero-rail-slot-count: 5;
          --hero-rail-width: max(
            168px,
            min(280px, calc(100vh / 5))
          );
          --hero-rail-hamburger-height: var(--hero-rail-width);
          /* Taller than the original 60vh so the generous top+bottom
             padding below has real room to breathe (rather than just eating
             into the content area). */
          height: 72vh;
          height: 72svh;
          height: 72dvh;
          max-height: 800px;
          min-height: 560px;
        }
        @supports (height: 100dvh) {
          .hero-solid-hero {
            --hero-rail-width: max(
              168px,
              min(280px, calc(100dvh / 5))
            );
          }
        }
        /* The header is fixed and transparent over this hero, so pad the
           vertically-centered content down past the floating navbar (plus any
           offer banner) to keep the eyebrow/title from sliding behind it.
           padding-top and padding-bottom scale together so the content stays
           comfortably below the header while the CTA gets clear separation
           from the section boundary — padding-top always exceeds padding-bottom
           so the vertically-centered title never creeps upward toward the nav. */
        .hero-solid-title-wrap {
          padding-top: calc(var(--offer-banner-height, 0px) + 8rem);
          padding-bottom: 4.5rem;
        }
        @media (min-width: 768px) {
          .hero-solid-title-wrap {
            padding-top: calc(var(--offer-banner-height, 0px) + 11rem);
            padding-bottom: 6.5rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-solid-title-wrap {
            padding-top: calc(var(--offer-banner-height, 0px) + 12rem);
            padding-bottom: 7.5rem;
          }
        }
        .hero-solid-title {
          font-family: "Raleway", sans-serif;
          font-size: 42px;
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0;
          max-width: 20ch;
        }
        .hero-solid-title p,
        .hero-solid-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-solid-title { font-size: 64px; }
        }
        @media (min-width: 1024px) {
          .hero-solid-title { font-size: 72px; }
        }

        /* Eyebrow + headline treatment (matches the reference inner-page header). */
        .hero-solid-eyebrow {
          font-family: "Raleway", sans-serif;
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #fff;
          margin: 0 0 0.9rem;
        }
        @media (min-width: 768px) {
          .hero-solid-eyebrow { font-size: 0.95rem; }
        }
        .hero-solid-eyebrow p,
        .hero-solid-eyebrow span {
          font: inherit;
          color: inherit;
          letter-spacing: inherit;
          text-transform: inherit;
          margin: 0;
        }
        .hero-solid-headline {
          font-family: "Raleway", sans-serif;
          font-size: 44px;
          font-weight: 700;
          line-height: 1.02;
          letter-spacing: 0;
          color: #fff;
          text-shadow: 0 2px 24px rgba(0, 0, 0, 0.3);
          margin: 0;
          max-width: 16ch;
        }
        .hero-solid-headline p,
        .hero-solid-headline span {
          font: inherit;
          color: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-solid-headline { font-size: 68px; }
        }
        @media (min-width: 1024px) {
          .hero-solid-headline { font-size: 80px; }
        }

        /* Positions the shared .site-cta pill under the hero eyebrow/title. */
        .hero-solid-cta-wrap {
          margin-top: 1.9rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
        }
        .hero-solid-cta-wrap:hover {
          box-shadow: 0 14px 36px rgba(0, 0, 0, 0.32);
        }

        .hero-solid-blurb {
          background: #F2F2F2;
        }
        .hero-solid-blurb-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: start;
        }
        @media (min-width: 768px) {
          .hero-solid-blurb-grid {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 3rem;
            align-items: center;
          }
        }
        @media (min-width: 1024px) {
          .hero-solid-blurb-grid {
            gap: 5rem;
          }
        }
        .hero-solid-blurb-heading {
          font-family: "Raleway", sans-serif;
          font-size: 2.25rem;
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.01em;
          color: var(--vibe-color-text);
          margin: 0;
          max-width: 12ch;
        }
        @media (min-width: 768px) {
          .hero-solid-blurb-heading { font-size: 3rem; }
        }
        @media (min-width: 1024px) {
          .hero-solid-blurb-heading { font-size: 3.75rem; }
        }
        .hero-solid-blurb-heading p,
        .hero-solid-blurb-heading span {
          font: inherit;
          color: inherit;
          letter-spacing: inherit;
          margin: 0;
        }
        .hero-solid-blurb-body-col {
          color: var(--vibe-color-text);
        }
        .hero-solid-blurb-text p {
          margin: 0;
        }
        .hero-solid-blurb-text p + p {
          margin-top: 1rem;
        }

        /* Pill CTAs beneath the blurb heading (Contact Us, Schedule a Tour).
           Stacked vertically in a single-column grid. The track is sized to
           max-content of the widest sibling (the phone number is usually
           longer than "Email Us"), and each pill fills the track — so both
           buttons render at the same width regardless of label length. */
        .hero-solid-blurb-ctas {
          display: grid;
          grid-template-columns: minmax(0, max-content);
          justify-content: start;
          gap: 1rem;
          margin-top: 1.75rem;
        }
        .hero-solid-blurb-ctas .site-cta {
          width: 100%;
          /* Left-align icon+label inside the pill so the icons pin to the
             same x-position across stacked buttons regardless of label
             length. Both pills share the same grid track width, so
             flex-start on the content lines the icons up perfectly. */
          justify-content: flex-start;
        }
        /* Email CTA overrides the shared left-align rule so its icon+label
           sit centered inside the pill (per-page treatment). */
        .hero-solid-blurb-ctas .hero-solid-blurb-email-cta {
          justify-content: center;
          text-align: center;
        }
        .hero-solid-blurb-cta-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          /* Fixed square keeps every icon glyph on the same column even if
             one SVG's path uses less of its 24×24 canvas than another. */
          width: 18px;
          height: 18px;
          flex: 0 0 18px;
        }
        .hero-solid-blurb-cta-icon > svg {
          display: block;
          margin: auto;
        }

        /* Mobile-only: center the single-column blurb layout. On md+ the
           two-column grid takes over and inherits the original left-aligned
           treatment via the media queries above. */
        @media (max-width: 767.98px) {
          .hero-solid-blurb-heading-col {
            text-align: center;
          }
          .hero-solid-blurb-heading {
            max-width: none;
            margin-left: auto;
            margin-right: auto;
          }
          .hero-solid-blurb-ctas {
            justify-content: center;
          }
          .hero-solid-blurb-body-col {
            text-align: center;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
    'herosolidbg.blurb_heading': {
      label: 'Blurb Section Heading',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Page hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'herosolidbg.cta_link': {
      label: 'Hero CTA Button',
      fields: {
        label: {
          label: 'Button Text',
          type: 'text',
          defaultValue: '',
        },
        url: {
          label: 'Button URL',
          type: 'url',
          defaultValue: '#',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'herosolidbg.phone_cta': {
      label: 'Phone CTA',
      fields: {
        label: {
          label: 'Button Text',
          type: 'phone',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
          defaultValue: '',
        },
        url: {
          label: 'Phone Number',
          type: 'phone',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'herosolidbg.email_cta': {
      label: 'Email CTA',
      fields: {
        label: {
          label: 'Button Text',
          type: 'text',
          defaultValue: 'Email Us',
        },
        url: {
          label: 'Email Address',
          type: 'email',
          defaultField: 'property_email',
          suggestedFields: ['property_email'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
